/* Общие правила форм «Фотосессии из селфи», «Фотосессии товара», «Коллажа» и «Своего аватара».
   Форма сервера (GET /app/studio/apps/{id}/form) — единственный источник ключей заказа: контрол из спецификации
   (APP-SPEC-5 §4, §10, §12, §15) показывается, только когда его поле опубликовано, и пишет только в это поле.
   Контракт docs/59-studio-backend-contracts.md §1: `fields[]` шаблона — значения строкой; `screen_fields[]`
   контракта экрана — JSON-тип (`value_encoding: "json"`). Общий транспорт (studio-command.ts) пока держит входы
   строками, поэтому значение поля `screen_fields` хранится JSON-текстом: его надо разобрать перед POST (см. отчёт). */
import { studioFields, studioUnitPrice, type StudioForm } from '../../studio/studio-api';
import { record, rows, text } from '../../studio/studio-content';

export type FormField = StudioForm['fields'][number] & { value_encoding?: 'string' | 'json' };
export type Inputs = Record<string, string>;
type Setter = (update: (current: Inputs) => Inputs) => void;

/** Поля шаблона и поля контракта экрана одним списком; ключ шаблона важнее (covered[] в контракте). */
export function allFields(form: StudioForm | null | undefined): FormField[] {
  if (!form) return [];
  const own = new Set(form.fields.map(field => field.key));
  const screen = rows(record(form).screen_fields).flatMap(item => {
    const key = text(item.key);
    if (!/^[a-z][a-z0-9_]{0,63}$/.test(key) || own.has(key)) return [];
    const values = (Array.isArray(item.values) ? item.values : Array.isArray(item.enum) ? item.enum : []).map(String);
    return [
      {
        key,
        type: text(item.type) || 'json',
        required: item.required === true,
        values,
        upload: null,
        ...(typeof item.max_length === 'number' ? { max_length: item.max_length } : {}),
        value_encoding: 'json' as const,
      },
    ];
  });
  return [...form.fields, ...screen];
}
export const fieldOf = (form: StudioForm | null | undefined, key: string): FormField | undefined =>
  allFields(form).find(field => field.key === key);
export const hasField = (form: StudioForm | null | undefined, key: string) => !!fieldOf(form, key);
/** Первое опубликованное из синонимов ключа (документация и стенд расходятся в именах). */
export const firstField = (form: StudioForm | null | undefined, keys: readonly string[]) =>
  keys.map(key => fieldOf(form, key)).find(Boolean);
export const limitOf = (field: FormField | undefined, fallback: number) =>
  field?.max_length && field.max_length > 0 ? Math.min(field.max_length, fallback) : fallback;
/** Скалярное значение поля для показа и сравнения: у JSON-поля — разобранное. */
export function valueOf(form: StudioForm | null | undefined, inputs: Inputs, key: string | undefined): string {
  if (!key) return '';
  const raw = inputs[key] ?? '';
  if (!raw || fieldOf(form, key)?.value_encoding !== 'json') return raw;
  try {
    const value = JSON.parse(raw) as unknown;
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : raw;
  } catch {
    return raw;
  }
}
/** Значение поля-перечисления допустимо, только если его публикует форма. */
export const allowed = (field: FormField | undefined, value: string) => !!field && (!field.values.length || field.values.includes(value));

const isReference = (field: FormField) =>
  field.upload?.purpose === 'reference' || !!field.upload?.purposes?.includes('reference') || /^reference(_\d+)?$/.test(field.key);
/** Одиночные загрузки фото формы: поля шаблона и контракта экрана (`screen_fields[]` с type "upload", docs/59 §1).
    У «Фотосессии товара» на стенде фото 2–5 и референсы reference_1…9 приходят только контрактом экрана;
    список `upload_list` — не слот (его значение — массив). */
function photoUploads(form: StudioForm | null | undefined) {
  return studioFields(form).filter(field => field.upload?.kind === 'photo' && field.type !== 'upload_list');
}
/** Слоты фото входа (товар, свои снимки), по порядку ключей; референсы — отдельно. */
export function photoSlots(form: StudioForm | null | undefined) {
  return photoUploads(form)
    .filter(field => !isReference(field))
    .sort((a, b) => a.key.localeCompare(b.key, 'en', { numeric: true }));
}
export function referenceSlots(form: StudioForm | null | undefined) {
  return photoUploads(form)
    .filter(field => isReference(field))
    .sort((a, b) => a.key.localeCompare(b.key, 'en', { numeric: true }));
}

/** Структурное значение в строку поля: у перечисления — сама строка, иначе JSON. */
export function encode(field: FormField | undefined, value: unknown): string | undefined {
  if (!field || value === undefined || value === null || value === '') return undefined;
  if (field.value_encoding === 'json') {
    const numeric = ['integer', 'number', 'int'].includes(field.type) && typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value);
    return JSON.stringify(numeric ? Number(value) : value);
  }
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && field.values.length) return value.map(String).join(',');
  return JSON.stringify(value);
}
export function decode<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    const value = JSON.parse(raw) as unknown;
    return (value ?? fallback) as T;
  } catch {
    return fallback;
  }
}
/** Список id из поля: JSON-массив или перечисление через запятую. */
export function listOf(raw: string | undefined): string[] {
  if (!raw) return [];
  const parsed = decode<unknown>(raw, null);
  if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  return raw.split(',').map(value => value.trim()).filter(Boolean);
}
/** Пишет значение только в опубликованное поле; пустое — убирает ключ (в заказ не уходит). */
export function writeField(setInputs: Setter, form: StudioForm | null | undefined, key: string, value: unknown) {
  const field = fieldOf(form, key);
  if (!field) return;
  const encoded = encode(field, value);
  setInputs(current => {
    const next = { ...current };
    if (encoded === undefined || encoded === '' || encoded === '[]') delete next[key];
    else next[key] = encoded;
    return next;
  });
}

/** Обязательные опубликованные поля, которые экран сам не заполняет: кнопка называет причину, а не молчит. */
export function requiredGap(form: StudioForm | null | undefined, inputs: Inputs, handled: readonly string[] = []): string | null {
  const field = allFields(form).find(item => item.required && !handled.includes(item.key) && !inputs[item.key]?.trim());
  return field ? 'Заполните, что нужно для создания' : null;
}

/** Цена за единицу: прайс сервера (unit_price за кадр/вариант), иначе данные экрана или ограничения формы; null — сервер её не публикует.
    Ноль документа экрана у платной формы — демо-оформление, не цена: по нему кнопка показала бы «0 C». */
export function unitPrice(form: StudioForm | null | undefined, screenData: Record<string, unknown>, screen: string): number | null {
  const listed = studioUnitPrice(form, 'variant');
  if (listed !== null) return listed;
  const constraints = record(record(form?.constraints)[screen]);
  const candidates = [constraints.unit_price, record(constraints.price).amount, screenData.unit_price, record(screenData.price).amount, screenData.price];
  const value = candidates.find(item => typeof item === 'number' && Number.isFinite(item) && (item > 0 || (item === 0 && !form?.chargeable)));
  return typeof value === 'number' ? value : null;
}
export function unitWord(screenData: Record<string, unknown>, fallback: string) {
  const price = record(screenData.price);
  return typeof price.unit_text === 'string' && price.unit_text ? price.unit_text : fallback;
}

/** Итог на кнопке. Сервер принимает заказ только по своей цене (expected_price): если состав меняет цену,
    а форма подтверждает другую, кнопка честно блокируется (use-studio-form шлёт expected_price формы). */
export function orderPrice(form: StudioForm, unit: number | null, quantity: number) {
  const total = unit !== null && form.chargeable ? unit * quantity : form.expected_price;
  const confirmed = !form.chargeable || unit === null || total === form.expected_price;
  return { total, confirmed };
}

const PLURAL = new Intl.PluralRules('ru');
export function plural(count: number, one: string, few: string, many: string) {
  const rule = PLURAL.select(count);
  return `${count} ${rule === 'one' ? one : rule === 'few' ? few : many}`;
}

/** Значение ячейки «Съёмка»: «Авто», пока ни одна строка не выбрана явно. */
export const shootSummary = (raw: string | undefined) => {
  if (!raw || raw === '{}') return 'Авто';
  try {
    const value = JSON.parse(raw) as Record<string, string>;
    return Object.values(value).some(id => !['auto', 'style', 'ref', 'open'].includes(id)) ? 'Выбрано' : 'Авто';
  } catch {
    return 'Авто';
  }
};
